/**
 * Web Crypto helpers aligned with backend AesGcmCrypto / RsaCrypto / CryptoSignCanonical.
 */

const GCM_TAG_BYTES = 16;
const AES_KEY_BITS = 256;

export type CryptoEnvelope = {
    cipherText: string;
    iv: string;
    tag: string;
};

function b64Encode(bytes: ArrayBuffer | Uint8Array): string {
    const u8 = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
    let s = '';
    for (let i = 0; i < u8.length; i++) s += String.fromCharCode(u8[i]);
    return btoa(s);
}

function b64Decode(b64: string): Uint8Array {
    const bin = atob(b64);
    const out = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
    return out;
}

function b64UrlEncode(bytes: Uint8Array): string {
    return b64Encode(bytes).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

function b64UrlDecode(s: string): Uint8Array {
    const pad = s.length % 4 === 0 ? '' : '='.repeat(4 - (s.length % 4));
    const b64 = s.replace(/-/g, '+').replace(/_/g, '/') + pad;
    return b64Decode(b64);
}

export function pemOrSpkiToArrayBuffer(pemOrSpki: string): ArrayBuffer {
    const cleaned = pemOrSpki
        .replace(/-----BEGIN PUBLIC KEY-----/g, '')
        .replace(/-----END PUBLIC KEY-----/g, '')
        .replace(/\s+/g, '');
    const bytes = b64Decode(cleaned);
    return bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
}

export async function importServerPublicKey(pemOrSpki: string): Promise<CryptoKey> {
    return crypto.subtle.importKey(
        'spki',
        pemOrSpkiToArrayBuffer(pemOrSpki),
        { name: 'RSA-OAEP', hash: 'SHA-256' },
        false,
        ['encrypt'],
    );
}

export async function generateAesKeyRaw(): Promise<Uint8Array> {
    const key = await crypto.subtle.generateKey({ name: 'AES-GCM', length: AES_KEY_BITS }, true, [
        'encrypt',
        'decrypt',
    ]);
    return new Uint8Array(await crypto.subtle.exportKey('raw', key));
}

export async function wrapAesKeyRsaOaep(serverPublicKey: CryptoKey, aesKeyRaw: BufferSource): Promise<string> {
    const wrapped = await crypto.subtle.encrypt({ name: 'RSA-OAEP' }, serverPublicKey, aesKeyRaw);
    return b64Encode(wrapped);
}

export async function generateClientRsaKeyPair(): Promise<CryptoKeyPair> {
    return crypto.subtle.generateKey(
        {
            name: 'RSASSA-PKCS1-v1_5',
            modulusLength: 2048,
            publicExponent: new Uint8Array([1, 0, 1]),
            hash: 'SHA-256',
        },
        true,
        ['sign', 'verify'],
    );
}

export async function exportSpkiBase64(publicKey: CryptoKey): Promise<string> {
    const spki = await crypto.subtle.exportKey('spki', publicKey);
    return b64Encode(spki);
}

export async function importAesKey(raw: BufferSource): Promise<CryptoKey> {
    return crypto.subtle.importKey('raw', raw, { name: 'AES-GCM' }, false, ['encrypt', 'decrypt']);
}

export async function encryptAesGcm(aesKey: CryptoKey, plain: BufferSource): Promise<CryptoEnvelope> {
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const packed = new Uint8Array(await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, aesKey, plain));
    const ct = packed.slice(0, packed.length - GCM_TAG_BYTES);
    const tag = packed.slice(packed.length - GCM_TAG_BYTES);
    return {
        cipherText: b64Encode(ct),
        iv: b64Encode(iv),
        tag: b64Encode(tag),
    };
}

export async function decryptAesGcm(aesKey: CryptoKey, envelope: CryptoEnvelope): Promise<Uint8Array> {
    const iv = b64Decode(envelope.iv);
    const ct = b64Decode(envelope.cipherText);
    const tag = b64Decode(envelope.tag);
    const packed = new Uint8Array(ct.length + tag.length);
    packed.set(ct, 0);
    packed.set(tag, ct.length);
    return new Uint8Array(await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, aesKey, packed));
}

export async function encryptPathToken(aesKey: CryptoKey, plaintextPath: string): Promise<string> {
    const env = await encryptAesGcm(aesKey, new TextEncoder().encode(plaintextPath));
    const iv = b64Decode(env.iv);
    const tag = b64Decode(env.tag);
    const ct = b64Decode(env.cipherText);
    const all = new Uint8Array(iv.length + tag.length + ct.length);
    all.set(iv, 0);
    all.set(tag, iv.length);
    all.set(ct, iv.length + tag.length);
    return b64UrlEncode(all);
}

export async function decryptPathToken(aesKey: CryptoKey, pathToken: string): Promise<string> {
    const all = b64UrlDecode(pathToken);
    const iv = all.slice(0, 12);
    const tag = all.slice(12, 12 + GCM_TAG_BYTES);
    const ct = all.slice(12 + GCM_TAG_BYTES);
    const plain = await decryptAesGcm(aesKey, {
        cipherText: b64Encode(ct),
        iv: b64Encode(iv),
        tag: b64Encode(tag),
    });
    return new TextDecoder().decode(plain);
}

export async function sha256Base64(data: BufferSource): Promise<string> {
    const hash = await crypto.subtle.digest('SHA-256', data);
    return b64Encode(hash);
}

export function buildRequestCanonical(parts: {
    method: string;
    wirePath: string;
    query: string;
    authorization: string;
    sessionId: string;
    requestId: string;
    timestamp: string;
    nonce: string;
    contentType: string;
    bodyDigestBase64: string;
}): string {
    return [
        parts.method.toUpperCase(),
        parts.wirePath ?? '',
        parts.query ?? '',
        parts.authorization ?? '',
        parts.sessionId ?? '',
        parts.requestId ?? '',
        parts.timestamp ?? '',
        parts.nonce ?? '',
        parts.contentType ?? '',
        parts.bodyDigestBase64 ?? '',
    ].join('\n');
}

export function buildResponseCanonical(parts: {
    requestId: string;
    nonce: string;
    bodyDigestBase64: string;
    pathCipher: string;
}): string {
    return [parts.requestId ?? '', parts.nonce ?? '', parts.bodyDigestBase64 ?? '', parts.pathCipher ?? ''].join('\n');
}

export async function signCanonical(privateKey: CryptoKey, canonical: string): Promise<string> {
    const sig = await crypto.subtle.sign(
        { name: 'RSASSA-PKCS1-v1_5' },
        privateKey,
        new TextEncoder().encode(canonical),
    );
    return b64Encode(sig);
}

export async function verifyCanonical(
    publicKey: CryptoKey,
    canonical: string,
    signatureBase64: string,
): Promise<boolean> {
    try {
        return await crypto.subtle.verify(
            { name: 'RSASSA-PKCS1-v1_5' },
            publicKey,
            b64Decode(signatureBase64),
            new TextEncoder().encode(canonical),
        );
    } catch {
        return false;
    }
}

export function randomId(): string {
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

export function isCryptoEnvelope(value: unknown): value is CryptoEnvelope {
    if (!value || typeof value !== 'object') return false;
    const v = value as Record<string, unknown>;
    return typeof v.cipherText === 'string' && typeof v.iv === 'string' && typeof v.tag === 'string';
}
